<template>
  <coozzy-card :title="$t('message.marketingSettings.appointmentReminders.title')">
    <b-card-sub-title>
      {{ $t('message.marketingSettings.appointmentReminders.subTitle') }}
    </b-card-sub-title>
    <div
      v-if="loading"
      class="row">
      <div class="col text-center">
        <coozzy-spinner />
      </div>
    </div>
    <template v-else>
      <div class="row mt-3">
        <div class="col-sm-4 col-md-3 mb-2">
          <label>{{ $t('message.marketingSettings.appointmentReminders.mailbox') }}</label>
          <coozzy-form-select
            v-model="selectedMailbox"
            :disabled="readOnly">
            <option value="">
              -
            </option>
            <option
              v-for="mailbox in mailBoxList"
              :key="mailbox.id"
              :value="mailbox.id">
              {{ mailbox.outboundEmail }}
            </option>
          </coozzy-form-select>
        </div>
      </div>
      <div class="row mt-2 align-items-end">
        <div class="col-auto mb-2">
          <label class="mr-2">{{ $t('message.marketingSettings.appointmentReminders.sendingTime') }}</label>
          <coozzy-form-input-numeric
            v-model="hoursBeforeAppointment"
            :check-valide="$v.hoursBeforeAppointment && $v.hoursBeforeAppointment.$dirty && $v.hoursBeforeAppointment.$error ? 'is-invalid' : ''"
            class="hours-input"
            :is-integer="true"
            :is-positive="true"
            :is-read-only="readOnly" />
        </div>
        <div class="col-auto mb-2">
          {{ $t('message.marketingSettings.appointmentReminders.hoursBefore') }}
        </div>
      </div>
      <div class="row">
        <div class="col">
          <coozzy-button
            v-if="isCompanyAdmin"
            design="primary"
            class="float-right"
            @click="saveClicked">
            {{ $t('message.generic.form.save') }}
          </coozzy-button>
        </div>
      </div>
    </template>
  </coozzy-card>
</template>

<script>
import { required, minValue } from 'vuelidate/lib/validators'
import CoozzyCard from '@/framework/components/card/CoozzyCard'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect.vue'
import CoozzyFormInputNumeric from '@/framework/components/form/input/CoozzyFormInputNumeric'
import CoozzyButton from '@/framework/components/button/CoozzyButton'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner'
import CalendarSchedulerApi from '@/misc/apis/CalendarSchedulerApi'
import MessageApi from '@/misc/apis/MessageApi'
import { user } from '@/mixins/user'
import Vue from 'vue'

export default {
  name: 'AppointmentRemindersSettings',
  components: {
    CoozzySpinner,
    CoozzyButton,
    CoozzyFormInputNumeric,
    CoozzyFormSelect,
    CoozzyCard
  },
  mixins: [user],
  data() {
    return {
      loading: false,
      selectedMailbox: '',
      hoursBeforeAppointment: null,
      mailBoxList: []
    }
  },
  computed: {
    readOnly() {
      return !this.isCompanyAdmin
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    loadData() {
      this.loading = true
      Promise.all([
        this.loadMailboxes(),
        this.loadReminderConfig()
      ]).then(() => {
        this.reconcileSelectedMailbox()
      }).finally(() => {
        this.loading = false
      })
    },
    loadMailboxes() {
      const request = this.isCompanyAdmin
        ? MessageApi.listMailboxes(this.accountId)
        : MessageApi.listMailboxesByUserId(this.$store.getters['user/getUserId'])
      return request
        .then(response => {
          this.mailBoxList = (response.mailboxes || []).filter(mailbox => mailbox.active)
        })
        .catch(() => {
          Vue.toasted.show(this.$t('message.loadingErrors.mailbox'), { type: 'error' })
        })
    },
    loadReminderConfig() {
      return CalendarSchedulerApi.getReminderConfig(this.accountId)
        .then(response => {
          this.selectedMailbox = response.mailboxId || ''
          this.hoursBeforeAppointment = response.hoursBeforeAppointment > 0
            ? response.hoursBeforeAppointment
            : null
        })
        .catch(e => {
          if (!e.response || e.response.status !== 404) {
            Vue.toasted.show(this.$t('message.loadingErrors.generic'), { type: 'error' })
          }
        })
    },
    reconcileSelectedMailbox() {
      if (!this.selectedMailbox) {
        return
      }
      const stillActive = this.mailBoxList.some(mailbox => mailbox.id === this.selectedMailbox)
      if (!stillActive) {
        this.selectedMailbox = ''
      }
    },
    saveClicked() {
      if (this.$v) {
        this.$v.$touch()
        if (this.$v.$invalid) {
          return
        }
      }

      this.loading = true
      CalendarSchedulerApi.setReminderConfig({
        accountId: this.accountId,
        hoursBeforeAppointment: this.selectedMailbox ? this.hoursBeforeAppointment : 0,
        mailboxId: this.selectedMailbox || ''
      }).then(() => {
        Vue.toasted.show(this.$t('message.marketingSettings.appointmentReminders.savedSuccessfully'), { type: 'success' })
      }).catch(() => {
        Vue.toasted.show(this.$t('message.marketingSettings.appointmentReminders.savedFailed'), { type: 'error' })
      }).finally(() => {
        this.loading = false
      })
    }
  },
  validations() {
    if (this.selectedMailbox) {
      return {
        hoursBeforeAppointment: {
          required,
          minValue: minValue(1)
        }
      }
    }
    return {}
  }
}
</script>

<style lang="scss" scoped>
.hours-input {
  display: inline-block;
  width: 100px;
  :deep(input) {
    height: auto;
  }
}
</style>
